import { getPresetRange, formatISODate } from "./metricsPeriod";

/** Period keys the backend accepts directly as ?period= */
const API_PRESET_PERIODS = new Set([
  "this_month",
  "last_month",
  "last_3_months",
]);

function toStartISO(dateStr) {
  if (!dateStr) return "";
  if (dateStr.includes("T")) return dateStr;
  return `${dateStr}T00:00:00.000Z`;
}

function toEndISO(dateStr) {
  if (!dateStr) return "";
  if (dateStr.includes("T")) return dateStr;
  return `${dateStr}T23:59:59.999Z`;
}

/**
 * Build query params for GET /va/metrics.
 * When no period is selected, or All time / Clear, omit period — the API
 * rejects `period=all` (400). Empty params use the backend all-time range.
 */
export function buildVaMetricsQueryParams(filter = {}) {
  const input = typeof filter === "string" ? { period: filter } : filter || {};
  const period = input.period;
  if (!period || period === "all") return {};

  // Custom calendar range
  if (period === "custom" && input.startDate && input.endDate) {
    return {
      period: "custom",
      from: toStartISO(input.startDate),
      to: toEndISO(input.endDate),
    };
  }

  // Native API presets
  if (API_PRESET_PERIODS.has(period)) {
    return { period };
  }

  // UI presets (today, yesterday, last_week, last_quarter) → date range
  const [start, end] = getPresetRange(period);
  if (start && end) {
    return {
      period: "custom",
      from: toStartISO(formatISODate(start)),
      to: toEndISO(formatISODate(end)),
    };
  }

  return { period: "this_month" };
}

function coerceMetricNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function hasMetricFields(payload) {
  if (!payload || typeof payload !== "object") return false;
  return (
    payload.totalRevenue != null ||
    payload.totalCommission != null ||
    payload.total_revenue != null ||
    payload.total_commission != null ||
    payload.pendingPayout != null ||
    payload.pending_payout != null
  );
}

/** Unwrap { success, data }, { success, item }, or nested metrics objects. */
export function unwrapVaMetricsPayload(response) {
  if (response == null || typeof response !== "object") return {};

  let current = response;
  for (let depth = 0; depth < 4; depth += 1) {
    if (hasMetricFields(current)) {
      return current.metrics && typeof current.metrics === "object"
        ? current.metrics
        : current;
    }

    if (current.item != null && typeof current.item === "object") {
      current = current.item;
      continue;
    }

    if (
      Object.prototype.hasOwnProperty.call(current, "data") &&
      current.data != null &&
      typeof current.data === "object" &&
      !Array.isArray(current.data)
    ) {
      current = current.data;
      continue;
    }

    if (current.metrics != null && typeof current.metrics === "object") {
      current = current.metrics;
      continue;
    }

    break;
  }

  return current;
}

function mapMetricsFields(payload = {}) {
  return {
    period: payload?.period?.key ?? payload?.period ?? null,
    periodRange: payload?.period ?? null,
    totalRevenue: coerceMetricNumber(
      payload.totalRevenue ?? payload.total_revenue
    ),
    totalCommission: coerceMetricNumber(
      payload.totalCommission ?? payload.total_commission
    ),
    pendingPayout: coerceMetricNumber(
      payload.pendingPayout ?? payload.pending_payout
    ),
    paidOut: coerceMetricNumber(payload.paidOut ?? payload.paid_out),
    returnsDeduction: coerceMetricNumber(
      payload.returnsDeduction ?? payload.returns_deduction
    ),
    unitsSold: coerceMetricNumber(payload.unitsSold ?? payload.units_sold),
    ordersReceived: coerceMetricNumber(
      payload.ordersReceived ?? payload.orders_received
    ),
    ordersShipped: coerceMetricNumber(
      payload.ordersShipped ?? payload.orders_shipped
    ),
    currency: payload?.currency || "USD",
    commissionPercent:
      payload?.commissionPercent ?? payload?.commission_percent ?? null,
  };
}

/** Normalize GET /va/metrics response for distributor dashboard cards. */
export function normalizeVaMetricsResponse(response) {
  return mapMetricsFields(unwrapVaMetricsPayload(response));
}

const EMPTY_AUDIOLOGIST_METRICS = {
  totalOrders: 0,
  totalSpend: 0,
  averageOrderValue: 0,
  totalReturns: 0,
  returnsAmount: 0,
};

/** Normalize GET /va/audiologist/metrics response for dashboard cards. */
export function normalizeAudiologistMetricsResponse(response) {
  let payload = response;
  if (payload?.data && typeof payload.data === "object") {
    payload = payload.data;
  }
  if (!payload || typeof payload !== "object") {
    return { ...EMPTY_AUDIOLOGIST_METRICS };
  }

  return {
    totalOrders: coerceMetricNumber(payload.totalOrders),
    totalSpend: coerceMetricNumber(payload.totalSpend),
    averageOrderValue: coerceMetricNumber(payload.averageOrderValue),
    totalReturns: coerceMetricNumber(payload.totalReturns),
    returnsAmount: coerceMetricNumber(payload.returnsAmount),
    period: payload.period ?? null,
    startDate: payload.startDate ?? null,
    endDate: payload.endDate ?? null,
    currency: payload.currency || "USD",
  };
}

/** Read audiologist metrics from RTK Query hook data. */
export function extractAudiologistMetricsFromResult(data) {
  if (!data) return null;
  if (data.totalOrders != null || data.totalSpend != null) {
    return normalizeAudiologistMetricsResponse(data);
  }
  if (data.data) return extractAudiologistMetricsFromResult(data.data);
  return null;
}

/** Read metrics from RTK Query hook data regardless of nesting. */
export function extractVaMetricsFromResult(data) {
  if (!data) return null;
  if (hasMetricFields(data)) return mapMetricsFields(data);
  if (data.data) return extractVaMetricsFromResult(data.data);
  return null;
}

function coerceHospitalRow(row = {}) {
  return {
    hospitalName: String(row.hospitalName || row.hospital_name || "Unknown hospital").trim(),
    clinicName: String(row.clinicName || row.clinic_name || "").trim() || null,
    orderCount: coerceMetricNumber(row.orderCount ?? row.order_count),
    revenue: coerceMetricNumber(row.revenue),
    fromCoverage: Boolean(row.fromCoverage ?? row.from_coverage),
  };
}

/** Normalize GET /va/metrics/hospitals response. */
export function normalizeHospitalOrderVolumeResponse(response) {
  let payload = response;
  if (payload?.data && typeof payload.data === "object") {
    payload = payload.data;
  }
  if (!payload || typeof payload !== "object") {
    return {
      hospitals: [],
      totalHospitals: 0,
      totalOrders: 0,
      period: null,
      currency: "USD",
    };
  }

  const hospitals = Array.isArray(payload.hospitals)
    ? payload.hospitals.map(coerceHospitalRow)
    : [];

  return {
    hospitals,
    totalHospitals: coerceMetricNumber(
      payload.totalHospitals ?? payload.total_hospitals ?? hospitals.length
    ),
    totalOrders: coerceMetricNumber(
      payload.totalOrders ??
        payload.total_orders ??
        hospitals.reduce((sum, row) => sum + row.orderCount, 0)
    ),
    period: payload.period ?? null,
    currency: payload.currency || "USD",
  };
}

export function extractHospitalOrderVolumeFromResult(data) {
  if (!data) return null;
  if (Array.isArray(data.hospitals) || data.totalHospitals != null) {
    return normalizeHospitalOrderVolumeResponse(data);
  }
  if (data.data) return extractHospitalOrderVolumeFromResult(data.data);
  return null;
}
